import React, { useState, useEffect } from 'react';
import { get, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function InvoiceSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [signature, setSignature] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);

  useEffect(() => {
    get('/invoice-settings').then(data => {
      setForm(data);
    });
  }, []);

  const loadSignature = async () => {
    if (user?.role !== 'super_admin') return;

    try {
      const token =
        localStorage.getItem('bb_token');

      const response = await fetch(
        '/api/invoice-settings/signature',
        {
          headers: token
            ? {
                Authorization:
                  'Bearer ' + token
              }
            : {}
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
          'Signature could not be loaded'
        );
      }

      setSignature(data.data || null);
    } catch (err) {
      setError(
        err.message ||
        'Signature could not be loaded'
      );
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      loadSignature();
    }
  }, [user?.role]);

  const uploadSignature = async () => {
    if (!signatureFile) {
      setError(
        'Select the approved PNG signature image first'
      );
      return;
    }

    if (
      signatureFile.type !== 'image/png'
    ) {
      setError(
        'Only a PNG signature image is allowed'
      );
      return;
    }

    if (
      signatureFile.size >
      2 * 1024 * 1024
    ) {
      setError(
        'The signature image must not exceed 2 MB'
      );
      return;
    }

    if (
      signature &&
      !window.confirm(
        'Replace the active company signature? Existing signed documents will keep their original signature version.'
      )
    ) {
      return;
    }

    setSignatureUploading(true);
    setError('');
    setSuccess('');

    try {
      const body = new FormData();

      body.append(
        'signature',
        signatureFile
      );

      body.append(
        'signer_name',
        'Doris Gideon-Ayantola'
      );

      body.append(
        'signer_title',
        'Managing Director'
      );

      const token =
        localStorage.getItem('bb_token');

      const response = await fetch(
        '/api/invoice-settings/signature',
        {
          method: 'POST',
          headers: token
            ? {
                Authorization:
                  'Bearer ' + token
              }
            : {},
          body
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
          'Signature upload failed'
        );
      }

      setSignature(data.data);
      setSignatureFile(null);
      setSuccess(
        'Company signature uploaded securely'
      );

      const fileInput =
        document.getElementById(
          'company-signature-upload'
        );

      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      setError(
        err.message ||
        'Signature upload failed'
      );
    } finally {
      setSignatureUploading(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(''); setSuccess('');
    try {
      await put('/invoice-settings', form);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="spinner"></div>;

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Invoice Settings</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Controls how all invoices look and what information they show.
          </p>
        </div>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </div>

      {success && <div className="alert alert-success mb-md">{success}</div>}
      {error && <div className="alert alert-error mb-md">{error}</div>}

      {!isSuperAdmin && (
        <div className="alert alert-info mb-lg">
          You can view invoice settings but only the Super Admin can change them.
        </div>
      )}

      {/* Logo */}
      <div className="card mb-lg">
        <div className="card-hdr"><span className="card-title">Company Logo</span></div>
        <div className="card-body">
          <div style={{display:'flex',alignItems:'flex-start',gap:24}}>
            <div style={{flexShrink:0}}>
              {form.logo_url
                ? <img src={form.logo_url} style={{width:100,height:100,objectFit:'contain',border:'1px solid var(--border-light)',borderRadius:8,padding:8,background:'#fff'}} alt="Logo" />
                : <div style={{width:100,height:100,border:'2px dashed var(--border)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'var(--ink-light)',textAlign:'center',padding:8}}>No logo set</div>
              }
            </div>
            <div style={{flex:1}}>
              <div className="form-group">
                <label className="form-label">Logo URL</label>
                <input className="form-control"
                  placeholder="https://yourdomain.com/logo.png"
                  value={form.logo_url||''}
                  onChange={e => setForm({...form, logo_url:e.target.value})}
                  disabled={!isSuperAdmin} />
                <div className="form-hint">
                  Upload your logo to Google Drive, Dropbox, or any image host and paste the direct link here.
                  For Google Drive: right-click image → Share → Anyone with link → copy link, then change
                  <code style={{fontSize:10,background:'#f5f5f5',padding:'1px 4px',borderRadius:3}}>/file/d/FILE_ID/view</code> to
                  <code style={{fontSize:10,background:'#f5f5f5',padding:'1px 4px',borderRadius:3}}>/uc?id=FILE_ID</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="card mb-lg">
          <div className="card-hdr">
            <span className="card-title">
              Managing Director Electronic Signature
            </span>
          </div>

          <div className="card-body">
            <div style={{
              display:'grid',
              gridTemplateColumns:
                'minmax(220px, 320px) 1fr',
              gap:24,
              alignItems:'start'
            }}>
              <div>
                <div style={{
                  minHeight:150,
                  border:
                    '1px solid var(--border-light)',
                  borderRadius:8,
                  background:'#fff',
                  padding:12,
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'center'
                }}>
                  {signature?.data_url ? (
                    <img
                      src={signature.data_url}
                      alt="Active Managing Director signature"
                      style={{
                        display:'block',
                        width:'100%',
                        maxHeight:150,
                        objectFit:'contain'
                      }}
                    />
                  ) : (
                    <div style={{
                      color:'var(--ink-light)',
                      fontSize:12,
                      textAlign:'center'
                    }}>
                      No active signature uploaded
                    </div>
                  )}
                </div>

                {signature && (
                  <div
                    className="form-hint"
                    style={{marginTop:8}}
                  >
                    Active version uploaded{' '}
                    {new Date(
                      signature.uploaded_at
                    ).toLocaleString('en-NG')}
                  </div>
                )}
              </div>

              <div>
                <div className="alert alert-info mb-md">
                  This protected signature is applied only
                  when you explicitly authorize an eligible
                  document. Replacing it will not alter any
                  document signed previously.
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Signature PNG
                  </label>

                  <input
                    id="company-signature-upload"
                    type="file"
                    className="form-control"
                    accept="image/png,.png"
                    onChange={event => {
                      const file =
                        event.target.files?.[0] ||
                        null;

                      setSignatureFile(file);
                      setError('');
                    }}
                  />

                  <div className="form-hint">
                    PNG only, maximum 2 MB. Use the
                    approved transparent-background version.
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Signer
                    </label>
                    <input
                      className="form-control"
                      value="Doris Gideon-Ayantola"
                      disabled
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Title
                    </label>
                    <input
                      className="form-control"
                      value="Managing Director"
                      disabled
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={uploadSignature}
                  disabled={
                    !signatureFile ||
                    signatureUploading
                  }
                >
                  {signatureUploading
                    ? 'Uploading securely...'
                    : signature
                      ? 'Replace Active Signature'
                      : 'Upload and Activate Signature'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Info */}
      <div className="card mb-lg">
        <div className="card-hdr"><span className="card-title">Company Information</span></div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input className="form-control" value={form.company_name||''}
                onChange={e => setForm({...form,company_name:e.target.value})} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Tagline</label>
              <input className="form-control" value={form.company_tagline||''}
                onChange={e => setForm({...form,company_tagline:e.target.value})} disabled={!isSuperAdmin} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-control" value={form.company_address||''}
              onChange={e => setForm({...form,company_address:e.target.value})} disabled={!isSuperAdmin} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Main Phone</label>
              <input className="form-control" value={form.company_phone||''}
                onChange={e => setForm({...form,company_phone:e.target.value})} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Dispatch Phone</label>
              <input className="form-control" value={form.company_dispatch||''}
                onChange={e => setForm({...form,company_dispatch:e.target.value})} disabled={!isSuperAdmin} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Complaints Phone</label>
              <input className="form-control" value={form.company_complaints||''}
                onChange={e => setForm({...form,company_complaints:e.target.value})} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-control" value={form.company_website||''}
                onChange={e => setForm({...form,company_website:e.target.value})} disabled={!isSuperAdmin} />
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="card mb-lg">
        <div className="card-hdr"><span className="card-title">Bank / Payment Details</span></div>
        <div className="card-body">
          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Bank Name</label>
              <input className="form-control" value={form.bank_name||''}
                onChange={e => setForm({...form,bank_name:e.target.value})} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input className="form-control" value={form.bank_account_no||''}
                onChange={e => setForm({...form,bank_account_no:e.target.value})} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Name</label>
              <input className="form-control" value={form.bank_account_name||''}
                onChange={e => setForm({...form,bank_account_name:e.target.value})} disabled={!isSuperAdmin} />
            </div>
          </div>
          <div className="form-group" style={{maxWidth:200}}>
            <label className="form-label">Invoice Number Prefix</label>
            <input className="form-control" value={form.invoice_prefix||'BLC'}
              onChange={e => setForm({...form,invoice_prefix:e.target.value})} disabled={!isSuperAdmin} />
            <div className="form-hint">e.g. BLC → BLC-2026-0001</div>
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="card mb-lg">
        <div className="card-hdr"><span className="card-title">Terms & Conditions</span></div>
        <div className="card-body">
          {[1,2,3,4,5].map(n => (
            <div className="form-group" key={n}>
              <label className="form-label">Term {n}</label>
              <textarea className="form-control" rows={2}
                value={form['term_'+n]||''}
                onChange={e => setForm({...form,['term_'+n]:e.target.value})}
                disabled={!isSuperAdmin} />
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Footer Text</label>
            <input className="form-control" value={form.footer_text||''}
              onChange={e => setForm({...form,footer_text:e.target.value})} disabled={!isSuperAdmin} />
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <>

          {/* Purchase Order Settings */}
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,padding:20,marginBottom:20}}>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',color:'#556B2F',fontWeight:600,marginBottom:16}}>Purchase Order Settings</div>
            <div className="form-group">
              <label className="form-label">Default Delivery Address</label>
              <textarea className="form-control" rows={3} value={form.po_delivery_address||''}
                onChange={e=>setForm({...form,po_delivery_address:e.target.value})}
                placeholder="Default delivery address printed on all purchase orders..." />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Terms</label>
                <input className="form-control" value={form.po_payment_terms||''}
                  onChange={e=>setForm({...form,po_payment_terms:e.target.value})}
                  placeholder="e.g. 30 days net" />
              </div>
              <div className="form-group">
                <label className="form-label">Authorised By</label>
                <input className="form-control" value={form.po_authorised_by||''}
                  onChange={e=>setForm({...form,po_authorised_by:e.target.value})}
                  placeholder="e.g. Doris Gideon-Ayantola, Managing Director" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Standard Terms & Notes</label>
              <textarea className="form-control" rows={3} value={form.po_notes||''}
                onChange={e=>setForm({...form,po_notes:e.target.value})}
                placeholder="Standard terms and conditions printed on all purchase orders..." />
            </div>
          </div>

        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:32}}>
          <button className="btn btn-primary" onClick={submit} disabled={saving} style={{minWidth:160}}>
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
        </>
      )}
    </div>
  );
}
